/**
 * ebdLayout.ts — Pure layout engine for Entscheidungsbaumdiagramme (EBD).
 *
 * Converts a raw EBD source tree (E_####.json) into positioned nodes + routed
 * edges that the SVG renderer (EbdDiagram.tsx) draws. No DOM, no React — fully
 * deterministic so it can be unit tested.
 *
 * Structure of an EBD: a vertical "spine" of numbered decision steps. Each step
 * has sub-rows (branches). A branch either continues to a later step or
 * terminates in a result cluster (A0x) or the success end. Two shapes exist:
 *   - linear (E_0014): one branch continues to the NEXT step, the other ends in
 *     a cluster → straight spine, clusters on the right.
 *   - branching (E_0623): both branches continue, often jumping over many steps
 *     (10→400). Jump edges are routed through bypass lanes LEFT of the spine so
 *     they never cross intermediate nodes; multiple outgoing continue-edges get
 *     distinct exit slots so arrows and ja/nein labels don't overlap.
 * Several steps frequently share the same result_code (e.g. A07), so clusters
 * are de-duplicated into a single node with multiple incoming edges.
 */
import type { EbdSource } from "@/types";

export type EbdNodeKind = "root" | "decision" | "cluster" | "end";

export interface EbdNode {
  id: string;
  kind: EbdNodeKind;
  x: number;
  y: number;
  w: number;
  h: number;
  badge?: string; // step number "1", result code "A01", …
  lines: string[]; // wrapped main text
  subtitle?: string; // role line for the root node
}

export interface EbdPoint {
  x: number;
  y: number;
}

export interface EbdEdge {
  id: string;
  from: string;
  to: string;
  label: string; // "ja" | "nein" | ""
  kind: "spine" | "branch";
  points: EbdPoint[];
}

export interface EbdGraph {
  nodes: EbdNode[];
  edges: EbdEdge[];
  width: number;
  height: number;
  title: string; // chapter · section
  subtitle: string; // ebd_name
  code: string;
}

// ── geometry constants ───────────────────────────────────────────────────────
const DEC_W = 380;
const CLUSTER_W = 320;
const COL_GAP = 170;
const GAP_Y = 44;
const PAD_Y = 14;
const LINE_H = 20;
const BADGE_H = 24;
const MARGIN = 48;
const DEC_CHARS = 48;
const CLUSTER_CHARS = 40;
const END_W = 132;
const END_H = 48;
const LANE_W = 26; // horizontal distance between bypass lanes
const SLOT_SPREAD = 140; // max horizontal spread of exit slots on one node
const STUB = 18; // vertical stub before/after a lane run

/** Greedy word-wrap honouring explicit "\n" breaks. Always returns ≥1 line. */
export function wrapText(text: string, maxChars: number): string[] {
  const out: string[] = [];
  for (const raw of (text ?? "").split("\n")) {
    const words = raw.split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      out.push("");
      continue;
    }
    let line = "";
    for (const w of words) {
      if (line === "") line = w;
      else if ((line + " " + w).length <= maxChars) line += " " + w;
      else {
        out.push(line);
        line = w;
      }
    }
    if (line) out.push(line);
  }
  return out.length ? out : [""];
}

/** "E_0623_Lieferbeginn prüfen" → "Lieferbeginn prüfen" (202610-style names). */
export function cleanEbdName(name: string | null | undefined): string {
  if (!name) return "";
  return name.replace(/^E_\d{4}[_ ]*/, "").trim();
}

const decHeight = (bodyLines: number) => PAD_Y * 2 + BADGE_H + bodyLines * LINE_H;
const clusterHeight = (bodyLines: number) => PAD_Y * 2 + BADGE_H + bodyLines * LINE_H;
const rootHeight = (hasRole: boolean) => PAD_Y * 2 + BADGE_H + (hasRole ? LINE_H : 0);

interface Branch {
  result: boolean | null;
  subsequent: string | null;
  code: string | null;
  note: string | null;
}

const labelFor = (result: boolean | null) =>
  result === true ? "ja" : result === false ? "nein" : "";

/** A continue-branch (no result_code) resolved to step indices. */
interface SpineRef {
  fromIdx: number;
  toIdx: number; // index into decisions, or decisions.length for the end node
  branchIdx: number;
  label: string;
  lane: number; // -1 = adjacent (no lane), >=0 = bypass lane
  slot: number; // exit-slot index among the node's continue-branches
  slotCount: number;
}

export function layoutEbd(src: EbdSource): EbdGraph {
  const meta = src.metadata ?? ({} as EbdSource["metadata"]);
  const code = meta.ebd_code ?? "EBD";
  // Effective role: raw `role` when set and != "N/A", otherwise the derived
  // role from the Prüfid register (flagged "abgeleitet"). Never show a bare "N/A".
  const rawRole = meta.role ?? "";
  const hasRawRole = rawRole !== "" && rawRole !== "N/A";
  const derivedRole = meta.derived_role ?? "";
  const roleSubtitle = hasRawRole
    ? `Prüfende Rolle: ${rawRole}`
    : derivedRole
      ? `Prüfende Rolle: ${derivedRole} (abgeleitet)`
      : undefined;
  const title = [meta.chapter, meta.section].filter(Boolean).join(" · ");
  const subtitle = cleanEbdName(meta.ebd_name);

  const rows = src.rows ?? [];
  const idxOf = new Map<string, number>(rows.map((r, i) => [String(r.step_number), i]));
  const endIdx = rows.length; // virtual index of the end node

  const branchesOf: Branch[][] = rows.map((row) =>
    (row.sub_rows ?? []).map((sr) => ({
      result: sr.check_result?.result ?? null,
      subsequent: sr.check_result?.subsequent_step_number ?? null,
      code: sr.result_code ?? null,
      note: sr.note ?? null,
    })),
  );

  // ── pass 1: continue-edges, exit slots, bypass lanes ─────────────────────────
  const spineRefs: SpineRef[] = [];
  branchesOf.forEach((branches, i) => {
    const continues = branches.filter((b) => !b.code);
    let slot = 0;
    branches.forEach((b, j) => {
      if (b.code) return;
      const toIdx =
        b.subsequent != null && idxOf.has(String(b.subsequent))
          ? idxOf.get(String(b.subsequent))!
          : endIdx; // "Ende" or unresolved → end node
      spineRefs.push({
        fromIdx: i,
        toIdx,
        branchIdx: j,
        label: labelFor(b.result),
        lane: -1,
        slot: slot++,
        slotCount: continues.length,
      });
    });
  });

  // jump edges (non-adjacent) get bypass lanes; greedy interval colouring,
  // short spans first so inner lanes stay close to the spine
  const skips = spineRefs
    .filter((e) => e.toIdx !== e.fromIdx + 1)
    .sort((a, b) => Math.abs(a.toIdx - a.fromIdx) - Math.abs(b.toIdx - b.fromIdx));
  const laneIntervals: [number, number][][] = [];
  for (const s of skips) {
    const lo = Math.min(s.fromIdx, s.toIdx);
    const hi = Math.max(s.fromIdx, s.toIdx);
    let lane = 0;
    while (laneIntervals[lane]?.some(([a, b]) => lo < b && a < hi)) lane++;
    (laneIntervals[lane] ??= []).push([lo, hi]);
    s.lane = lane;
  }
  const laneCount = laneIntervals.length;

  // the decision column starts right of the bypass lanes
  const decX = MARGIN + 8 + laneCount * LANE_W;
  const clusterX = decX + DEC_W + COL_GAP;
  const laneX = (lane: number) => decX - 20 - lane * LANE_W;

  const nodes: EbdNode[] = [];
  const edges: EbdEdge[] = [];

  // ── root ───────────────────────────────────────────────────────────────────
  const root: EbdNode = {
    id: "root",
    kind: "root",
    x: decX,
    y: MARGIN,
    w: DEC_W,
    h: rootHeight(!!roleSubtitle),
    badge: code,
    lines: [],
    subtitle: roleSubtitle,
  };
  nodes.push(root);

  // ── decision spine ───────────────────────────────────────────────────────────
  let y = root.y + root.h + GAP_Y;
  const decisions: EbdNode[] = [];
  for (const row of rows) {
    const lines = wrapText(row.description ?? "", DEC_CHARS);
    const node: EbdNode = {
      id: `step-${row.step_number}`,
      kind: "decision",
      x: decX,
      y,
      w: DEC_W,
      h: decHeight(lines.length),
      badge: row.step_number,
      lines,
    };
    nodes.push(node);
    decisions.push(node);
    y += node.h + GAP_Y;
  }

  // ── collect terminal references (dedup clusters by result_code) ──────────────
  const termRefs = new Map<string, { note: string | null; ys: number[] }>();
  decisions.forEach((node, i) => {
    for (const b of branchesOf[i]) {
      if (!b.code) continue;
      const cy = node.y + node.h / 2;
      const existing = termRefs.get(b.code);
      if (existing) {
        existing.ys.push(cy);
        if (!existing.note && b.note) existing.note = b.note;
      } else {
        termRefs.set(b.code, { note: b.note, ys: [cy] });
      }
    }
  });

  // place clusters ordered by mean reference-y, resolving vertical overlaps
  const avg = (xs: number[]) => xs.reduce((a, c) => a + c, 0) / xs.length;
  const clusterNode = new Map<string, EbdNode>();
  let clusterBottom = root.y;
  const orderedCodes = [...termRefs.entries()].sort((a, b) => avg(a[1].ys) - avg(b[1].ys));
  for (const [c, ref] of orderedCodes) {
    const body = wrapText(ref.note ?? "", CLUSTER_CHARS);
    const h = clusterHeight(body.length);
    const desired = avg(ref.ys) - h / 2;
    const cy = Math.max(desired, clusterBottom + GAP_Y);
    const node: EbdNode = {
      id: `cluster-${c}`,
      kind: "cluster",
      x: clusterX,
      y: cy,
      w: CLUSTER_W,
      h,
      badge: c,
      lines: body,
    };
    clusterNode.set(c, node);
    nodes.push(node);
    clusterBottom = cy + h;
  }

  // ── end node (lazy, single, at the bottom of the spine) ──────────────────────
  let endNode: EbdNode | null = null;
  const ensureEnd = (): EbdNode => {
    if (!endNode) {
      endNode = {
        id: "end",
        kind: "end",
        x: decX + DEC_W / 2 - END_W / 2,
        y,
        w: END_W,
        h: END_H,
        lines: ["Ende"],
      };
      nodes.push(endNode);
      y += END_H + GAP_Y;
    }
    return endNode;
  };

  // ── edges ────────────────────────────────────────────────────────────────────
  // terminal branches → right-hand clusters
  decisions.forEach((node, i) => {
    branchesOf[i].forEach((b, j) => {
      if (!b.code) return;
      const cl = clusterNode.get(b.code)!;
      edges.push(branchEdge(node, cl, labelFor(b.result), `${i}-${j}`));
    });
  });

  // continue branches → spine / lane edges
  for (const ref of spineRefs) {
    const from = decisions[ref.fromIdx];
    const to = ref.toIdx === endIdx ? ensureEnd() : decisions[ref.toIdx];
    // exit slot: spread multiple continue-exits across the node bottom
    const spread = Math.min(SLOT_SPREAD, DEC_W / Math.max(ref.slotCount, 1));
    const sx = from.x + from.w / 2 + (ref.slot - (ref.slotCount - 1) / 2) * spread;
    const sy = from.y + from.h;
    const tx = to.x + to.w / 2;
    const ty = to.y;
    let points: EbdPoint[];
    if (ref.lane >= 0) {
      // bypass lane left of the spine — never crosses intermediate nodes
      const lx = laneX(ref.lane);
      const y1 = sy + STUB;
      const y2 = ty - STUB;
      points = [
        { x: sx, y: sy },
        { x: sx, y: y1 },
        { x: lx, y: y1 },
        { x: lx, y: y2 },
        { x: tx, y: y2 },
        { x: tx, y: ty },
      ];
    } else if (Math.abs(sx - tx) < 1) {
      points = [
        { x: sx, y: sy },
        { x: sx, y: ty },
      ];
    } else {
      // adjacent target, offset exit → elbow; jitter midY per slot so parallel
      // same-target edges don't share a horizontal run
      const midY = (sy + ty) / 2 + (ref.slot - (ref.slotCount - 1) / 2) * 8;
      points = [
        { x: sx, y: sy },
        { x: sx, y: midY },
        { x: tx, y: midY },
        { x: tx, y: ty },
      ];
    }
    edges.push({
      id: `s-${from.id}-${to.id}-${ref.fromIdx}-${ref.branchIdx}`,
      from: from.id,
      to: to.id,
      label: ref.label,
      kind: "spine",
      points,
    });
  }

  const bottom = Math.max(...nodes.map((n) => n.y + n.h));
  const right = Math.max(...nodes.map((n) => n.x + n.w));
  return {
    nodes,
    edges,
    width: right + MARGIN,
    height: bottom + MARGIN,
    title,
    subtitle,
    code,
  };
}

/** Branch edge: out to the right into a result cluster. Orthogonal elbow. */
function branchEdge(from: EbdNode, to: EbdNode, label: string, key: string): EbdEdge {
  const startX = from.x + from.w;
  const startY = from.y + from.h / 2;
  const endX = to.x;
  const endY = to.y + to.h / 2;
  const midX = (startX + endX) / 2;
  const points: EbdPoint[] = [
    { x: startX, y: startY },
    { x: midX, y: startY },
    { x: midX, y: endY },
    { x: endX, y: endY },
  ];
  return { id: `b-${from.id}-${to.id}-${key}`, from: from.id, to: to.id, label, kind: "branch", points };
}
